import React, { lazy, Suspense, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";
import {
  NAVIGATION,
  GROUPS,
  FLAT_LIMIT,
  drawerEntries,
} from "../constants/navigation";
import AccountMenu from "./AccountMenu";
import ShopSwitcher from "./ShopSwitcher";
import Chevron from "./Chevron";
import HubTabs from "./HubTabs";
import LanguageSelect from "./LanguageSelect";
import NewOrderAlert from "./NewOrderAlert";
import SubscriptionBanner from "./SubscriptionBanner";
import SupportButton from "./SupportButton";
import ErrorBoundary from "./ErrorBoundary";
import EmailVerificationBanner from "./EmailVerificationBanner";
import HelpButton from "../help/HelpButton";
import useHelp, { HelpOpenerContext } from "../help/useHelp";
import OnboardingWizard from "./OnboardingWizard";
import { SECTIONS, isSectionOffered } from "../help/sections";


// All the help's words in three languages: fetched when it is first opened,
// not with the register that a cashier needs to open quickly
const HelpPanel = lazy(() => import("../help/HelpPanel"));

/** The drawer is permanent from this width up; below it, it covers the page. */
const DESKTOP = 1024;

const Layout = () => {
  // A phone opens on the screen, not on a menu that hides it
  const [sidebarOpen, setSidebarOpen] = useState(
    () => window.innerWidth >= DESKTOP,
  );
  const { user, logout } = useAuth();
  const { t, locale } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  // The shop's own name, not the product's. Two shops running the same
  // software should never both see somebody else's name over their sidebar.
  const { shop, membership, subscription, isOffered, can, moduleOn, flag } = useMembership();
  const help = useHelp();

  // The help follows the same rule as the drawer: only what this member has
  const helpSections = SECTIONS.filter((section) =>
    isSectionOffered(section, { can, moduleOn, flag }),
  );

  /**
   * Only the screens this member has been given. That mirrors what the API
   * already enforces — it is not the security boundary, the server is — and
   * stops someone walking into a wall of refusals and asking the owner why
   * the app is broken.
   */
  const offered = NAVIGATION.filter(isOffered);
  const entries = drawerEntries(offered);
  const accountItems = offered.filter((item) => item.placement === "account");

  // Permanent on desktop, but a page picked from the drawer on a phone should
  // reveal the screen behind it rather than leaving the drawer covering it.
  const closeOnMobile = () => {
    if (window.innerWidth < DESKTOP) setSidebarOpen(false);
  };

  const isActive = (path) => {
    if (path === "/admin")
      return location.pathname.replace(/\/$/, "") === "/admin";
    return location.pathname.startsWith(path);
  };

  const isEntryActive = (entry) =>
    entry.screens.some((screen) => isActive(screen.path));

  // A member with few screens gets them as they are. Headings earn their place
  // only once the list would otherwise have to be scrolled.
  const grouped = entries.length > FLAT_LIMIT;
  const activeGroup = entries.find(isEntryActive)?.group || null;

  // One section open at a time, and it is the one holding the current page:
  // the drawer stays short, and never hides where you are.
  // A choice made by hand holds only while the page stays in the same section;
  // going elsewhere falls back to the section that page is in.
  const [choice, setChoice] = useState(null);
  const openGroup =
    choice && choice.from === activeGroup ? choice.group : activeGroup;

  const linkClass = (active) =>
    `flex items-center px-4 py-3 rounded-lg transition-all ${
      active
        ? "bg-white text-primary-600 shadow-lg"
        : "text-white hover:bg-primary-700"
    }`;

  const renderEntry = (entry) => (
    <Link
      key={entry.id}
      to={entry.path}
      onClick={closeOnMobile}
      className={linkClass(isEntryActive(entry))}
    >
      <span className="text-2xl me-3">{entry.icon}</span>
      <span className="font-medium">{t(entry.labelKey)}</span>
    </Link>
  );

  // What someone does in this shop says more than their account type
  const role = membership?.role || user?.role;

  return (
    <HelpOpenerContext.Provider value={help.openHelp}>
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar — slides in from the start edge, which is the right in Arabic */}
      <div
        className={`fixed inset-y-0 start-0 z-50 w-64 bg-gradient-to-b from-primary-600 to-primary-800 transform transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-6 py-4 border-b border-primary-500">
            <ShopSwitcher />
            <button
              onClick={() => setSidebarOpen(false)}
              aria-label={t("nav.closeMenu")}
              className="lg:hidden text-white hover:text-primary-200"
            >
              ✕
            </button>
          </div>

          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {grouped
              ? [
                  ...entries.filter((entry) => !entry.group).map(renderEntry),
                  ...GROUPS.map((group) => {
                    const members = entries.filter(
                      (entry) => entry.group === group,
                    );
                    // A section with nothing offered — every module in it
                    // switched off, say — leaves no empty heading behind
                    if (members.length === 0) return null;
                    const open = openGroup === group;
                    return (
                      <div key={group}>
                        <button
                          type="button"
                          aria-expanded={open}
                          onClick={() =>
                            setChoice({
                              group: open ? null : group,
                              from: activeGroup,
                            })
                          }
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-start transition-all ${
                            members.some(isEntryActive) && !open
                              ? "bg-primary-700 text-white"
                              : "text-primary-100 hover:bg-primary-700"
                          }`}
                        >
                          <span className="text-xs font-bold uppercase tracking-wider">
                            {t(`nav.groups.${group}`)}
                          </span>
                          <Chevron direction={open ? "down" : "end"} />
                        </button>
                        {open && (
                          <div className="mt-1 space-y-1">
                            {members.map(renderEntry)}
                          </div>
                        )}
                      </div>
                    );
                  }),
                ]
              : entries.map(renderEntry)}
          </nav>

          <div className="px-4 pb-2">
            <Link
              to="/"
              className="flex items-center justify-center px-4 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-bold shadow-lg transition-all"
            >
              <span className="text-xl me-2">🧾</span>
              {t("nav.openRegister")}
            </Link>
          </div>

          <div className="px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-primary-500">
            <AccountMenu
              user={user}
              role={role}
              items={accountItems}
              isActive={isActive}
              onNavigate={closeOnMobile}
              onLogout={logout}
            />
          </div>
        </div>
      </div>

      <div
        className={`transition-all duration-300 ${
          sidebarOpen ? "lg:ms-64" : "ms-0"
        }`}
      >
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-6 py-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={t("nav.menu")}
              className="shrink-0 text-gray-600 hover:text-gray-900 focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
              <span className="hidden md:inline text-sm text-gray-600 truncate">
                {new Date().toLocaleDateString(locale, {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
              <LanguageSelect />
              <SupportButton />
              <HelpButton
                onClick={help.openHelp}
                hasNews={help.hasNews}
                open={help.open}
              />
              <Link
                to="/"
                className="lg:hidden shrink-0 px-2 sm:px-3 py-2 rounded-lg bg-emerald-500 text-white text-sm font-bold shadow"
              >
                🧾 {t("nav.register")}
              </Link>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6">
          <SubscriptionBanner shopId={shop?.id} notice={subscription} />
          <EmailVerificationBanner />
          <HubTabs offered={offered} />
          <ErrorBoundary compact resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {help.loaded && (
        <Suspense fallback={null}>
          <HelpPanel
            open={help.open}
            onClose={help.closeHelp}
            sections={helpSections}
            pathname={location.pathname}
            hasNews={help.hasNews}
            onWhatsNewRead={help.markWhatsNewSeen}
            initialTopic={help.topic}
          />
        </Suspense>
      )}

      <OnboardingWizard />

      <NewOrderAlert
        currency={shop?.currency || "TND"}
        onView={() => navigate("/admin/preorders")}
      />
    </div>
    </HelpOpenerContext.Provider>
  );
};

export default Layout;
